import { forwardRef, type ComponentType } from "react";
import { useTheme } from "@mui/material/styles";

/**
 * Injects the active MUI theme as a `theme` prop.
 *
 * MUI v5 does still *export* `withTheme` from `@mui/material/styles`, but only as a
 * stub that throws on call — the real implementation lives in the deprecated
 * `@mui/styles`. This restores the v4 behaviour on top of `useTheme` so the
 * remaining mobx class components can keep reading the theme through props.
 *
 * `props` is spread after the injected theme, matching v4: an explicitly passed
 * `theme` still wins.
 */
const withTheme = <Props extends object>(Component: ComponentType<Props>) => {
  const WithTheme = forwardRef<unknown, Props>((props, ref) => {
    const theme = useTheme();
    const injected = { theme, ref, ...props } as unknown as Props;

    return <Component {...injected} />;
  });

  WithTheme.displayName = `WithTheme(${Component.displayName || Component.name || "Component"})`;

  return WithTheme;
};

export default withTheme;
